import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { AdPolicy } from '@lenovo/tan-client-bridge-le';
import { DeviceSettingsFeatureToggle, FeatureToggle } from 'src/app/enums/feature-toggle.enum';
import { VantageShellService } from '../vantage-shell/vantage-shell.service';
import { ADPolicyState } from 'src/app/constants/ad-policy.constants';

import { IFeatureToggle } from './feature-toggle.interface';
@Injectable({
	providedIn: 'root',
})
export class FeatureToggleService implements IFeatureToggle {
	adPolicyService: AdPolicy;

	featureTable: { [feature: string]: boolean | string } = {
		modernPreload: true,
		warranty: true,
	};

	categoryTable: { [category: string]: boolean | string } = {
		device: true,
		user: true,
	};

	pageTable: { [page: string]: boolean } = {
		dashboard: true,
		myDevice: true,
		deviceSetting: true,
		systemUpdate: true,
		smartAssist: true,
		supportDetail: true,
		mySecurity: true,
		antiVirus: true,
		vpn: true,
		passwordProtection: true,
		wifiSecurity: true,
		hardwareScan: true,
		getSupport: true,
		smartPerformance: true,
		homeSecurity: true,
		appForYou: true,
		preferenceSetting: true,
		feedback: true,
		modernPreload: true
	};

	private _deviceSettingsPolicyTable: any = {};
	public get deviceSettingsPolicyTable() {
		return this._deviceSettingsPolicyTable;
	}

	constructor(private vantageShellService: VantageShellService) {
		_.set(this.featureTable, FeatureToggle.GiveFeedback, true);
	}

	async initialize() {
		await this.getAdPolicy();
	}

	private async getAdPolicy() {
		const adPolicyBridge = this.vantageShellService.getAdPolicy();
		if (!adPolicyBridge) {
			return;
		}

		const adTags = await adPolicyBridge.tags;
		if (!adTags || JSON.stringify(adTags) === '{}') {
			return;
		}

		Object.keys(adTags).map((key) => {
			const splitedKey = key.split('.');
			const [type, tagKey] = splitedKey;
			const adTagValue = adTags[key];

			if (!type || !tagKey) {
				return;
			}

			if (type === 'page') {
				this.setPagePolicy(tagKey, adTagValue);
			} else if (type === 'feature') {
				this.setFeaturePolicy(tagKey, splitedKey, adTagValue);
			} else if (type === 'category') {
				this.setCategoryPolicy(tagKey, adTagValue);
			}
		});
	}

	private setCategoryPolicy(value: string, adTagValue: any) {
		if (this.categoryTable[value] !== undefined) {
			this.categoryTable[value] = adTagValue;
		}
	}

	private setFeaturePolicy(key: string, splitedKey: string[], adTagValue: any) {
		if (key === DeviceSettingsFeatureToggle.DeviceSettings) {
			const deviceSettingsPath = splitedKey.slice(2, splitedKey.length);
			_.set(this._deviceSettingsPolicyTable, deviceSettingsPath, adTagValue);
		} else if (this.featureTable[key] !== undefined) {
			this.featureTable[key] = adTagValue;
		}
	}

	private setPagePolicy(value: string, adTagValue: any) {
		if (this.pageTable[value] !== undefined) {
			this.pageTable[value] = adTagValue;
		}
	}

	isFeatureAvailable(feature: string): boolean | string {
		const result = this.featureTable[feature];
		if (result === undefined) {
			return false;
		}
		return result;
	}

	isPageAvailable(page: string): boolean {
		return Boolean(this.pageTable[page]);
	}

	isCategoryAvailable(category: string): boolean {
		return Boolean(this.categoryTable[category]);
	}

	isAvailableWithFullName(fullName: string): boolean | string | undefined {
		const typeValueArr = fullName.split('.');
		if (typeValueArr.length !== 2) {
			return undefined;
		}

		if (typeValueArr[0].toLowerCase() === 'page') {
			return this.isPageAvailable(typeValueArr[1]);
		}

		if (typeValueArr[0].toLowerCase() === 'feature') {
			return this.isFeatureAvailable(typeValueArr[1]);
		}

		if (typeValueArr[0].toLowerCase() === 'category') {
			return this.isCategoryAvailable(typeValueArr[1]);
		}
	}

	isDeviceSettingsFeatureAvailable(deviceSettingsFeature: string): boolean {
		const featurePolicyValue = _.get(this._deviceSettingsPolicyTable, deviceSettingsFeature);
		const isFeatureAvailable = featurePolicyValue === ADPolicyState.DISABLED;
		const notConfigured = featurePolicyValue === ADPolicyState.NOT_CONFIGURED;

		return notConfigured || isFeatureAvailable;
	}

	//this method would return the latest adpolicy value
	async getAdPolicyByName(name): Promise<boolean> {
		var adPolicyBridge = this.vantageShellService.getAdPolicy();
		if (!adPolicyBridge) {
			return Promise.resolve(undefined);
		}
		const policyValue = await adPolicyBridge.getAdPolicyById(name);
		const iseAvailable = +policyValue === 0;
		const notConfigured = policyValue === ADPolicyState.NOT_CONFIGURED;

		return Promise.resolve(notConfigured || iseAvailable);
	}
}
